// ============================================================
// OLD REFUND DETAIL PAGE (commented out)
// ============================================================
// import React, { useCallback, useEffect, useState, useMemo } from "react";
// import classes from "./refundDetailPage.module.css";
// import { refundDetailTableColumnsConfig } from "./refundDetailTableColumnsConfig";
// import { useParams } from "react-router-dom";
// import { useDispatch } from "react-redux";
// import {
//   useGetOrderDetailQuery,
//   useUpdateShipmentStatusMutation,
// } from "../../../services/orderDetailApiSlice";
// import { Shipment } from "./shipment/Shipment";
// import { RefundAddressPage } from "./refundAddress/RefundAddressPage";
// import { CategoryPageSkeleton } from "../../../component/skeleton/CategoryPageSkeleton";
// import { toast } from "react-toastify";
//
// export const RefundDetailPage = () => {
//   const params = useParams();
//   const orderId = params.refundId;
//   const dispatch = useDispatch();
//   const [columnDefs, setColumnDefs] = useState([]);
//
//   const { data, isSuccess } = useGetOrderDetailQuery(orderId, { skip: !orderId });
//
//   const [
//     updateShipmentStatus,
//     { isLoading: isUpdatingStatus, isSuccess: isStatusUpdated },
//   ] = useUpdateShipmentStatusMutation();
//
//   const handleOpenModal = useCallback((rowData, action) => {
//     console.log("Row Data: ", rowData, "Action: ", action);
//   }, []);
//
//   const handleStatusChange = async ({ shipment_id, status }) => {
//     if (!orderId || !shipment_id || !status) {
//       toast.error("Missing required parameters to update shipment status.");
//       return;
//     }
//     const loadingToast = toast.loading("Updating status...");
//     try {
//       const result = await updateShipmentStatus({ orderId, id: shipment_id, status }).unwrap();
//       toast.update(loadingToast, {
//         render: result?.message?.displayMessage || "Shipment status updated successfully!",
//         type: "success",
//         isLoading: false,
//         autoClose: 2000,
//       });
//     } catch (error) {
//       toast.update(loadingToast, {
//         render: error?.message?.displayMessage || "Failed to update shipment status. Please try again.",
//         type: "error",
//         isLoading: false,
//         autoClose: 2000,
//       });
//       console.error(error);
//     }
//   };
//
//   useEffect(() => {
//     setColumnDefs(refundDetailTableColumnsConfig["refundDetail"](handleOpenModal));
//   }, [dispatch, handleOpenModal, orderId]);
//
//   return isSuccess ? (
//     <div className={classes.box}>
//       <RefundAddressPage address={data?.orderDetails} />
//       <div className={classes.box__order__detail}>
//         {data.shipmentDetails.map((shipment) => (
//           <Shipment key={shipment?.shipmentKey} shipment={shipment} onChange={handleStatusChange} />
//         ))}
//       </div>
//     </div>
//   ) : (
//     <CategoryPageSkeleton />
//   );
// };
// ============================================================

import { useState } from "react";
import classes from "./refundDetailPage.module.css";
import { useParams } from "react-router-dom";

import {
  useGetOrderDetailQuery,
  useUpdateShipmentStatusMutation,
} from "../../../services/orderDetailApiSlice";

import { Shipment } from "./shipment/Shipment";
import { RefundAddressPage } from "./refundAddress/RefundAddressPage";
import { CategoryPageSkeleton } from "../../../component/skeleton/CategoryPageSkeleton";

import { toast } from "react-toastify";
import { ConfirmModal } from "../../../component/confirmModal/ConfirmModal";

import {
  CheckCircleIcon,
  TruckIcon,
  ClockIcon,
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

export const RefundDetailPage = () => {
  const { refundId } = useParams();
  const orderId = refundId;
  const [confirmModal, setConfirmModal] = useState(null);

  const { data, isSuccess } = useGetOrderDetailQuery(orderId, {
    skip: !orderId,
    refetchOnMountOrArgChange: true,
  });

  const [updateShipmentStatus] = useUpdateShipmentStatusMutation();

  const handleStatusChangeRequest = (data) => {
    setConfirmModal(data);
  };

  const handleConfirmStatusChange = async () => {
    if (!confirmModal) return;

    const loadingToast = toast.loading("Updating status...");

    try {
      const result = await updateShipmentStatus({
        orderId,
        id: confirmModal.shipment_id,
        status: confirmModal.status,
      }).unwrap();

      toast.update(loadingToast, {
        render:
          result?.message?.displayMessage ||
          "Shipment status updated successfully!",
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });
    } catch (error) {
      toast.update(loadingToast, {
        render:
          error?.data?.message?.displayMessage ||
          "Failed to update shipment status.",
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
    }

    setConfirmModal(null);
  };

  return isSuccess ? (
    <div className={classes.box}>
      <h2 className={classes.box__title}>Refund Detail</h2>

      <div className={classes.page}>
        <RefundAddressPage address={data?.orderDetails} />

        <div className={`${classes.card} ${classes.cardHighlight}`}>
          <h3 className={classes.sectionTitle}>
            Shipments ({data?.shipmentDetails?.length || 0})
          </h3>

          <div className={classes.shipmentList}>
            {data?.shipmentDetails?.map((shipment, index) => (
              <div
                key={shipment?.shipmentKey}
                className={`${classes.shipmentWrapper} ${
                  index % 2 === 0 ? classes.shipmentEven : classes.shipmentOdd
                }`}
              >
                <Shipment
                  shipment={shipment}
                  orderId={orderId}
                  onChange={handleStatusChangeRequest}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {confirmModal && (
        <ConfirmModal
          title="Change Shipment Status"
          message="Are you sure you want to change this shipment status?"
          onConfirm={handleConfirmStatusChange}
          onCancel={() => setConfirmModal(null)}
          status={confirmModal.currentStatus}
          nextStatus={confirmModal.statusLabel}
          currentStatus={confirmModal.currentStatus}
          shipment_id={confirmModal.shipment_id}
          orderId={orderId}
        />
      )}
    </div>
  ) : (
    <CategoryPageSkeleton />
  );
};

export const ShipmentStatusBadge = ({ status, showTitle = true }) => {
  if (!status) return null;

  const key = status.toLowerCase().trim();

  const statusConfig = {
    delivered: {
      label: "Delivered",
      bg: "#E6F7EF",
      color: "#00A167",
      Icon: CheckCircleIcon,
    },
    shipped: {
      label: "Shipped",
      bg: "#E0F2FE",
      color: "#0284C7",
      Icon: TruckIcon,
    },
    "in process": {
      label: "In Process",
      bg: "#FEF3C7",
      color: "#D97706",
      Icon: ClockIcon,
    },
    inprocess: {
      label: "In Process",
      bg: "#FEF3C7",
      color: "#D97706",
      Icon: ClockIcon,
    },
    "out for delivery": {
      label: "Out for Delivery",
      bg: "#E8F0FE",
      color: "#2563EB",
      Icon: TruckIcon,
    },
    "refund in process": {
      label: "Refund in Process",
      bg: "#EEF2FF",
      color: "#6366F1",
      Icon: ArrowPathIcon,
    },
    refund: {
      label: "Refunded",
      bg: "#F3F4F6",
      color: "#4B5563",
      Icon: ArrowUturnLeftIcon,
    },
    rejected: {
      label: "Rejected",
      bg: "#FDECEC",
      color: "#E53935",
      Icon: XCircleIcon,
    },
    pending: {
      label: "Pending",
      bg: "#FFF4E5",
      color: "#FF8C00",
      Icon: ClockIcon,
    },
  };

  const config = statusConfig[key];
  if (!config) return null;

  const { label, bg, color, Icon } = config;

  return (
    <div className={classes.status}>
      {showTitle && <h4 className={classes.status__title}>Status:</h4>}

      <div
        className={classes.status__badge}
        style={{
          backgroundColor: bg,
          color,
        }}
      >
        <Icon style={{ width: 14, height: 14 }} />
        {label}
      </div>
    </div>
  );
};
